import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router";
import { removeProjectsByUuids, setSelectedProjects } from "src/redux";
import { SelectedTasksBar } from "src/app/components/SelectedTasksBar/SelectedTasksBar";
import { Button } from "src/app/components/Button/Button";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { ReactComponent as ReportIcon } from "src/images/report-generator.svg";
import { ReactComponent as RubberIcon } from "src/images/rubber.svg";
import { RootState } from "src/redux/reducers";
import { MAX_CREATORS_FOR_GENERATE_SHOWCASE } from "src/app/CreatorShowcase/utils";
import TooltipPortal from "src/app/components/TooltipPortal/TooltipPortal";
import {
  openReportGenerator,
  ReportGeneratorContext,
} from "src/app/ReportGenerator/utils/reportFunctions";
import { showErrorToast } from "src/utils/methods";
import { API_URLS } from "src/utils/API_URLS";
import axios from "axios";
import { RemoveModal } from "src/app/modals/RemoveModal/RemoveModal";
import { WorkspacePermissionsEnum } from "src/utils/PermissionsEnums";
import { saveToLocalStorage } from "src/utils/localStorage";

export function SelectedProjectsBar() {
  const {
    mainReducer: { activeWorkspaceUuid, identity },
    projectReducer: { selectedProjects },
  } = useSelector((state: RootState) => state);

  const [isTooltipOpen, setIsTooltipOpen] = useState(
    window.localStorage.getItem("selectedProjectsBarTooltipHidden") === null
      ? true
      : !JSON.parse(
          window.localStorage.getItem(
            "selectedProjectsBarTooltipHidden",
          ) as string,
        ),
  );
  const [removalLoading, setRemovalLoading] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);

  const dispatch = useDispatch();
  const history = useHistory();

  const clearSelected = () => {
    dispatch(setSelectedProjects([]));
  };

  const hideTooltip = () => {
    saveToLocalStorage("selectedProjectsBarTooltipHidden", true);
    setIsTooltipOpen(false);
  };

  const openReport = () => {
    const ids = selectedProjects.map((project) => project.id);

    openReportGenerator({
      history,
      context: ReportGeneratorContext.CampaignList,
      workspaceId: activeWorkspaceUuid,
      ids,
    });
  };

  const confirmDeleting = async () => {
    if (!selectedProjects.length) return;
    setRemovalLoading(true);

    const uuidsToRemove = selectedProjects.map((project) => project.id);

    try {
      await axios.post(API_URLS.deleteMultipleProjects, {
        wsProjectsUuids: uuidsToRemove,
      });

      dispatch(removeProjectsByUuids(uuidsToRemove));
      dispatch(setSelectedProjects([]));
      setShowRemoveModal(false);
    } catch (err) {
      showErrorToast();
    } finally {
      setRemovalLoading(false);
    }
  };

  return (
    <SelectedTasksBar
      selectedTasksLength={selectedProjects.length}
      isTooltipOpen={isTooltipOpen}
      hideTooltip={hideTooltip}
    >
      <>
        <TooltipPortal
          content={
            <IDHFormattedMessage
              id="ws_cannot_generate_showcase"
              defaultMessage="Cannot generate showcase for more than 100 creators"
            />
          }
          contentHidden={
            selectedProjects.length <= MAX_CREATORS_FOR_GENERATE_SHOWCASE
          }
          up
        >
          <Button
            size="large"
            variant="white"
            onClick={openReport}
            data-action="generateReport"
          >
            <ReportIcon />
            <IDHFormattedMessage
              id="ws_generate_report"
              defaultMessage="Generate Report"
            />
          </Button>
        </TooltipPortal>
        <Button
          size="large"
          variant="white"
          onClick={clearSelected}
          disabled={!selectedProjects.length}
          data-action="clearSelected"
        >
          <RubberIcon />
          <IDHFormattedMessage
            id="ws_clear_selected"
            defaultMessage="Clear selected"
          />
        </Button>
        {identity.permissions?.workspace.includes(
          WorkspacePermissionsEnum.DELETE_PROJECTS,
        ) && (
          <Button
            size="large"
            variant="red-text"
            onClick={() => setShowRemoveModal(true)}
            disabled={!selectedProjects.length}
          >
            <RubberIcon />
            <IDHFormattedMessage id="ws_remove" defaultMessage="Remove" />
          </Button>
        )}
      </>
      {showRemoveModal && (
        <RemoveModal
          onClose={() => setShowRemoveModal(false)}
          objectNames={selectedProjects.map((project) => project.name)}
          removeFunction={confirmDeleting}
          loading={removalLoading}
        />
      )}
    </SelectedTasksBar>
  );
}
